import {
    Button,
    Caption,
    Flex,
    TextInput,
    useStyles,
    useTheme,
} from '@archipro-design/aria';
import { Loader, SearchLine } from '@archipro-design/icons';
import type { SyntheticEvent } from 'react';
import React, { useState } from 'react';
import * as S from './ProductTaggingSearchModal.style';
import ProductSearchResultTile from '@modules/product-tagging/components/product-search-result-tile/ProductSearchResultTile';
import { Animation } from '@fluentui/react-northstar';
import {
    URL_SEARCH_PARAM,
    MIN_PROFESSIONAL_SEARCH_CHARACTERS,
    NETWORK_PROFESSIONAL_BLACKLIST,
} from '@modules/product-tagging/config/search-form';
import { useFetcher } from '@remix-run/react';
import { useDebounceFn } from 'ahooks';
import { ProductTaggingSupplierSearch } from '~/routes/remix-api.product-tagging.supplier-search';
import type {
    ProductTaggingSearchFormProps,
    TagDataUseToCreateTag,
} from '@modules/product-tagging/type';
import type { loader } from '~/routes/remix-api.product-tagging.supplier-search';
import { useProductTagging } from '@modules/product-tagging/context';

interface SupplierSearchFormProps
    extends Pick<ProductTaggingSearchFormProps, 'onConfirm'> {
    onClickButton: (e: React.SyntheticEvent<HTMLElement>) => void;
}

export const SupplierSearchForm = ({
    onClickButton,
    onConfirm,
}: SupplierSearchFormProps) => {
    const { css } = useStyles();
    const theme = useTheme();
    const { dispatch, recentlyTaggedProfessionals } = useProductTagging();

    const [keyword, setKeyword] = useState('');

    const fetcher = useFetcher<typeof loader>();

    const { run: searchSupplier } = useDebounceFn(
        (event: SyntheticEvent<HTMLElement, Event>) => {
            const target = event.target as HTMLInputElement;
            setKeyword(target.value);
            if (target.value.length >= MIN_PROFESSIONAL_SEARCH_CHARACTERS) {
                fetcher.load(
                    `${ProductTaggingSupplierSearch}?${URL_SEARCH_PARAM}=${target.value}`
                );
            }
        },
        {
            wait: 300,
            leading: true,
        }
    );

    const initiateSearch = keyword.length >= MIN_PROFESSIONAL_SEARCH_CHARACTERS;
    const supplierSearchResults = initiateSearch ? fetcher.data || [] : [];

    const excludeBlacklisted = supplierSearchResults.filter(
        (tile) => !NETWORK_PROFESSIONAL_BLACKLIST.includes(tile.id)
    );

    const loading = fetcher.state === 'loading';

    const onClickTile = (data: TagDataUseToCreateTag) => {
        dispatch({ type: 'TOGGLE_ADDING_SUPPLIER', payload: true });
        onConfirm(data);
    };

    return (
        <>
            <div className={css(S.SearchField)}>
                <TextInput
                    className={css(S.SearchTextInput)}
                    input={{
                        placeholder: 'Search product supplier',
                        autoFocus: true,
                    }}
                    size={'md'}
                    suffixIcons={[<SearchLine key={'searchIcon'} />]}
                    onChange={searchSupplier}
                />
            </div>
            <Flex column gap={'space-4'} className={css(S.SearchBody)}>
                {excludeBlacklisted.length > 0 ? (
                    excludeBlacklisted.map((tile) => (
                        <ProductSearchResultTile
                            key={tile.pType + tile.id}
                            tileData={tile}
                            showProfessionalName={false}
                            onConfirm={onClickTile}
                        />
                    ))
                ) : (
                    <>
                        {!initiateSearch &&
                        recentlyTaggedProfessionals.length > 0 ? (
                            <>
                                <Caption
                                    className={css(S.EmptyResults)}
                                    variant="04"
                                    variables={{
                                        captionColor:
                                            theme.siteVariables.colors
                                                .grey[300],
                                    }}
                                >
                                    Recently tagged
                                </Caption>
                                {recentlyTaggedProfessionals.map((tile) => (
                                    <ProductSearchResultTile
                                        key={tile.pType + tile.id}
                                        tileData={tile}
                                        showProfessionalName={false}
                                        onConfirm={onConfirm}
                                    />
                                ))}
                            </>
                        ) : (
                            <Caption
                                className={css(S.EmptyResults)}
                                variant="04"
                                variables={{
                                    captionColor:
                                        theme.siteVariables.colors.grey[300],
                                }}
                            >
                                No results
                            </Caption>
                        )}
                    </>
                )}
            </Flex>
            <div className={css(S.ModalFooter)}>
                <Button
                    color={'dark'}
                    type={'submit'}
                    size={16}
                    variables={{
                        buttonMinWidth: '100%',
                    }}
                    styles={{
                        textTransform: 'uppercase',
                        color: theme.siteVariables.colors.white['000'],
                    }}
                    onClick={onClickButton}
                >
                    {`Can't find supplier?`}
                </Button>
            </div>
            {loading && (
                <div className={css(S.SearchLoading)}>
                    <Animation name="rotateUltraSlow" className={css(S.Loader)}>
                        <Loader size="2x" />
                    </Animation>
                </div>
            )}
        </>
    );
};
